import "./Calendar.css";
import { useEffect, useRef, useState } from "react";
import { openJobQuickView, toAppointmentHours, useJobs } from "../../core/Global";
import { EventInput } from "@fullcalendar/core";
import moment from "moment";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import FlexRow from "../base/FlexRow";
import Icon from "../base/Icon";
import Job from "../../modules/crm/jobs/models/Job";
import { getJobStatusLightenColor } from "../../core/Helper";
import { jobMatchesKeyword } from "../../modules/crm/jobs/utils/jobFilters";


export type CalendarDateBasis = "appointment" | "created" | "lastStatus"


interface IProps {
    techId?: string
    /** The board header's applied filters (see modules/crm/jobs/utils/jobFilters). */
    filter?: (job: Job) => boolean
    findKeyword?: string
    dateBasis?: CalendarDateBasis
}
export default function JobsCalendar(props: IProps) {

    const jobsBank = useJobs()
    const calendarRef = useRef<FullCalendar | null>(null)
    const [events, eventsSet] = useState<EventInput[]>()
    const [boxHeight, boxHeightSet] = useState<number>()

    const dateBasis: CalendarDateBasis = props.dateBasis ?? "appointment"

    const fixSizes = () => {
        var w = $(window).width() ?? 500;
        var h = $(window).height() ?? 400;
        if (w < 500) {
            h = 250;
        }
        else {
            h = h - ($("#kt_header").outerHeight() ?? 50);
            h = h - ($("#kt_toolbar").outerHeight() ?? 50);
            h = h - 40;
        }
        boxHeightSet(h);
    }

    const jobsFilter = (job: Job) => {
        var res: Array<boolean> = [];

        if (props.filter) res.push(props.filter(job));

        if (props.findKeyword) res.push(jobMatchesKeyword(job, props.findKeyword));

        return res.every(f => f);
    }

    const resolveEventDate = (job: Job): string | undefined => {
        if (dateBasis === "created") {
            return job.createdAt ? moment(job.createdAt).format("YYYY-MM-DD HH:mm") : undefined;
        }
        if (dateBasis === "lastStatus") {
            return job.lastStatusUpdatedAt ? moment(job.lastStatusUpdatedAt).format("YYYY-MM-DD HH:mm") : undefined;
        }
        return job.appointmentDateForCalendar;
    }

    useEffect(() => {
        fixSizes();
        window.addEventListener("resize", fixSizes);
        return () => window.removeEventListener("resize", fixSizes);
    }, [])


    useEffect(() => {
        if (!jobsBank) return;

        let data = jobsBank.filter(jobsFilter);
        if (props.techId) data = data.filter(f => f.techId == props.techId);

        const eventsBuilt: EventInput[] = []
        let latestDate: moment.Moment | null = null;

        data.forEach(f => {
            const dt = resolveEventDate(f);
            if (!dt) return;
            const m = moment(dt);
            if (!m.isValid()) return;

            if (!latestDate || m.isAfter(latestDate)) latestDate = m;

            eventsBuilt.push({
                id: f.id,
                start: dt,
                allDay: false,
                backgroundColor: "transparent",
                borderColor: "transparent",
            });
        });

        eventsSet(eventsBuilt);

        if (latestDate && calendarRef.current) {
            try {
                calendarRef.current.getApi().gotoDate((latestDate as moment.Moment).toDate());
            } catch { }
        }

    }, [jobsBank, props.techId, props.filter, props.findKeyword, dateBasis])


    const getJob = (id?: string) => jobsBank?.filter(f => f.id == id)[0]


    return (
        <div>

            <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, bootstrap5Plugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,timeGridWeek"
                }}
                dayHeaderClassNames="fc-days-header"
                events={events} allDaySlot={false}
                height={boxHeight}
                eventBorderColor="transparent"
                eventContent={e => {

                    var job = getJob(e.event.id)
                    if (!job) return (<></>)

                    return (
                        <div onClick={e => {
                            e.preventDefault();
                            openJobQuickView(job?.id);
                        }} className="mb-0 bg-gray-300 text-gray-700 w-100 px-2 py-1 cursor-pointer"
                            style={{
                                border: `1px solid ${job.jobStatus?.color}`,
                                borderRadius: 10,
                                overflow: "hidden",
                                backgroundColor: getJobStatusLightenColor(job)
                            }}>
                            <FlexRow className="mb-1">
                                <FlexRow>
                                    <Icon icon="circle" type="solid" color={job.jobStatus?.color} size={10} className="me-1" />
                                    <div className="fs-9" style={{ marginTop: 1.5 }}>
                                        #{job.jobId}
                                    </div>
                                </FlexRow>
                                <FlexRow className="ms-auto" title="Appointment Time">
                                    <Icon icon="clock" type="solid" size={10} className="me-1" />
                                    <div className="fs-9" style={{ marginTop: 1.5 }}>
                                        {toAppointmentHours(job)}
                                    </div>
                                </FlexRow>
                            </FlexRow>
                            <FlexRow className="fs-8">
                                <FlexRow className="me-5">
                                    <Icon icon="user" type="solid" size={10} className="me-1" />
                                    <div className="fs-9 limited-text" style={{ marginTop: 1.5, width: 40 }}>
                                        {job.fullName}
                                    </div>
                                </FlexRow>
                                <FlexRow>
                                    <Icon icon="phone-rotary" type="solid" size={10} className="me-1" />
                                    <div className="fs-9 limited-text" style={{ marginTop: 1.5 }}>
                                        {job.phone?.phoneFriendly}
                                    </div>
                                </FlexRow>
                            </FlexRow>
                            <FlexRow className="fs-8">
                                <Icon icon="location-dot" type="solid" size={10} className="me-1" />
                                <div className="fs-9 limited-text" style={{ marginTop: 1.5 }}>
                                    {job.address?.title}
                                </div>
                            </FlexRow>
                            <FlexRow className="fs-8">
                                <Icon icon="user-helmet-safety" type="solid" size={10} className="me-1" />
                                <div className="fs-9 limited-text" style={{ marginTop: 1.5 }}>
                                    {job.tech?.fullName ?? "-"}
                                </div>
                            </FlexRow>
                        </div>
                    )

                }}
            />
        </div>
    )
}
